import api from './client'
import type { AdminConfig, Question } from '../types'

export interface AdminResponse {
  id: string
  file_id: string
  code: string
  status: 'draft' | 'submitted'
  is_published: boolean
  is_ai_submitted: boolean
  pmc_id: string | null
  doi: string | null
  title: string | null
  submitted_at: string | null
  updated_at: string
  fields: Record<string, unknown>
}

export const adminApi = {
  login: (password: string) =>
    api.post<{ access_token: string; token_type: string }>('/admin/login', { password }).then((r) => r.data),
  getConfig: () => api.get<AdminConfig>('/admin/config').then((r) => r.data),
  updateConfig: (config: Partial<AdminConfig>) =>
    api.put<AdminConfig>('/admin/config', config).then((r) => r.data),
  changePassword: (current_password: string, new_password: string) =>
    api.post('/admin/change-password', { current_password, new_password }).then((r) => r.data),

  listResponses: () => api.get<AdminResponse[]>('/admin/responses').then((r) => r.data),
  updateResponse: (id: string, data: Partial<AdminResponse>) =>
    api.put<AdminResponse>(`/admin/responses/${id}`, data).then((r) => r.data),
  deleteResponse: (id: string) => api.delete(`/admin/responses/${id}`).then((r) => r.data),
  uploadResponses: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post('/admin/responses/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data)
  },

  getQuestionnaire: () => api.get<Question[]>('/admin/questionnaire').then((r) => r.data),
  updateQuestionnaire: (questions: Question[]) =>
    api.put<Question[]>('/admin/questionnaire', questions).then((r) => r.data),
  uploadQuestionnaire: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post('/admin/questionnaire/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data)
  },
  addQuestion: (q: Partial<Question>) =>
    api.post<Question>('/admin/questionnaire/row', q).then((r) => r.data),
  deleteQuestion: (id: string) => api.delete(`/admin/questionnaire/${id}`).then((r) => r.data),

  regenerateReports: (scope: 'missing' | 'all') =>
    api.post('/admin/reports/regenerate', { scope }).then((r) => r.data),

  downloadBackup: () =>
    api.get('/admin/backup', { responseType: 'blob' }).then((r) => r.data as Blob),
  restoreBackup: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post<RestoreSummary>('/admin/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data)
  },

  // LLM benchmark against EV-TRACK.
  startBenchmark: (
    provider: string,
    judge_provider: string,
    opts: { model?: string; judge_model?: string; force_refresh?: boolean } = {},
  ) =>
    api
      .post<BenchmarkRun>('/admin/benchmark', {
        provider,
        judge_provider,
        model: opts.model || '',
        judge_model: opts.judge_model || '',
        force_refresh: opts.force_refresh || false,
      })
      .then((r) => r.data),
  benchmarkModels: () =>
    api.get<{ gemini: string[]; deepseek: string[] }>('/admin/benchmark-models').then((r) => r.data),
  listBenchmarks: () => api.get<BenchmarkRun[]>('/admin/benchmark').then((r) => r.data),
  getBenchmark: (id: string) => api.get<BenchmarkRun>(`/admin/benchmark/${id}`).then((r) => r.data),
  deleteBenchmark: (id: string) => api.delete(`/admin/benchmark/${id}`).then((r) => r.data),

  getFeedback: () => api.get<AiFeedbackRow[]>('/admin/feedback').then((r) => r.data),
  deleteFeedback: (id: string) => api.delete(`/admin/feedback/${id}`).then((r) => r.data),
  getSubmissionManuscript: (submissionId: string) =>
    api.get<SubmissionManuscript>(`/admin/submissions/${submissionId}/manuscript`).then((r) => r.data),

  // Report template preview (returns full HTML string).
  getReportPreview: () =>
    api.get<string>('/admin/report-preview', { responseType: 'text' }).then((r) => r.data),

  // Downloads (blob) — token is attached by the axios interceptor.
  downloadResponses: () =>
    api.get('/admin/responses-export', { responseType: 'blob' }).then((r) => r.data as Blob),
  downloadQuestionnaire: (format: 'csv' | 'xlsx') =>
    api
      .get('/admin/questionnaire/download', { params: { format }, responseType: 'blob' })
      .then((r) => r.data as Blob),

  // LLM extraction config editor.
  getExtractionConfig: (name: string) =>
    api
      .get<ExtractionConfig>('/admin/extraction-config', { params: { name } })
      .then((r) => r.data),
  saveExtractionConfig: (payload: { name: string; columns: string[]; rows: Record<string, unknown>[] }) =>
    api.put('/admin/extraction-config', payload).then((r) => r.data),
  downloadExtractionConfig: (name: string) =>
    api
      .get('/admin/extraction-config/download', { params: { name }, responseType: 'blob' })
      .then((r) => r.data as Blob),
  uploadExtractionConfig: (name: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api
      .post('/admin/extraction-config/upload', fd, {
        params: { name },
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },

  // Bulk PMC ingestion.
  startBulkPmc: (pmc_ids: string[], provider: string) =>
    api.post<BulkPmcRun>('/admin/bulk-pmc', { pmc_ids, provider }).then((r) => r.data),
  listBulkPmc: () => api.get<BulkPmcRun[]>('/admin/bulk-pmc').then((r) => r.data),
  getBulkPmc: (id: string) => api.get<BulkPmcRun>(`/admin/bulk-pmc/${id}`).then((r) => r.data),
  deleteBulkPmc: (id: string) => api.delete(`/admin/bulk-pmc/${id}`).then((r) => r.data),

  // Usage & traffic stats.
  getUsageStats: (params?: { start?: string; end?: string }) =>
    api.get<UsageStats>('/admin/stats/usage', { params }).then((r) => r.data),
  getTrafficStats: (params?: { start?: string; end?: string }) =>
    api.get<TrafficStats>('/admin/stats/traffic', { params }).then((r) => r.data),
}

export interface BulkPmcItem {
  pmc_id: string
  title: string
  status: 'created' | 'duplicate' | 'skipped' | 'error'
  file_id: string | null
  submission_id: string | null
  error: string
}

export interface BulkPmcRun {
  id: string
  provider: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: string
  total: number
  done: number
  results: BulkPmcItem[]
  error_message: string
  created_at: string
  completed_at: string | null
}

export interface ExtractionConfig {
  name: string
  filename: string
  columns: string[]
  rows: Record<string, string>[]
}

export interface RestoreSummary {
  detail: string
  tables: Record<string, number>
  files: Record<string, number>
}

export interface SubmissionManuscript {
  found: boolean
  input_type: string
  input_ref: string
  manuscript_text: string
  created_at?: string
}

export interface BenchmarkCategory {
  category: string
  n: number
  precision: number
  recall: number
  f1: number
  tp: number
  fp: number
  fn: number
  tn: number
}

export interface BenchmarkMetrics {
  provider: string
  model: string
  judge_provider: string
  judge_model: string
  skipped_papers: string[]
  n_papers: number
  n_fields: number
  n_agree: number
  pct_agree: number
  overall: { precision: number; recall: number; f1: number; agreement_pct: number }
  categories: BenchmarkCategory[]
  per_paper: { paper: string; n_fields: number; n_agree: number; pct: number }[]
  disagreement: { type: string; label: string; n: number; pct_of_total: number }[]
  fields: { field_label: string; category: string; n: number; precision: number; recall: number; f1: number }[]
  generated_at: string
}

export interface BenchmarkRun {
  id: string
  provider: string
  model: string
  judge_provider: string
  judge_model: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: string
  total_papers: number
  done_papers: number
  error_message: string
  metrics?: BenchmarkMetrics | null
  created_at: string
  completed_at: string
}

export interface AiFeedbackRow {
  id: string
  submission_id: string
  file_id: string
  code: string
  question_name: string
  question_label: string
  feedback_type: string
  original_value: string
  corrected_value: string
  comment: string
  was_ai_filled: boolean
  created_at: string
}

export interface DailyCount {
  date: string
  count: number
}

export interface StatsRange {
  start: string
  end: string
  days: number
}

export interface UsageStats {
  range: StatsRange
  submissions: { total: number; submitted: number; draft: number; published: number }
  period: { submissions: number; published: number; ai: number }
  by_source: { human: number; ai: number }
  ai_jobs: { pending: number; running: number; completed: number; failed: number }
  ai: {
    jobs: number
    completed: number
    failed: number
    success_rate: number | null
    avg_duration_sec: number | null
    avg_fields: number | null
    by_provider: { provider: string; count: number }[]
  }
  funnel: { started: number; submitted: number; published: number }
  completion: { count: number; avg_minutes: number | null; median_minutes: number | null }
  content: {
    ev_sources: { value: string; count: number }[]
    ev_applications: { value: string; count: number }[]
  }
  submissions_per_day: DailyCount[]
}

export interface TrafficPathCount {
  path: string
  count: number
}

export interface TrafficStats {
  range: StatsRange
  total_requests: number
  human_requests: number
  bot_requests: number
  unique_ips: number
  error_rate: number
  status_breakdown: { '2xx'?: number; '3xx'?: number; '4xx'?: number; '5xx'?: number }
  report_downloads: { pdf: number; html: number; total: number }
  top_paths: TrafficPathCount[]
  top_errors: { path: string; status: number; count: number }[]
  top_referrers: { referer: string; count: number }[]
  requests_per_day: DailyCount[]
  visitors_per_day: DailyCount[]
}
